// Fatia S1: aceitar convite (mostra a conta; cria senha se a pessoa ainda não tem login).
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { AcceptInvitationResponse, InvitationPreviewDto } from '@impulso/shared/api/tenancy'
import { api, ApiError, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import AuthShell, { FormError } from '../../features/auth/AuthShell'
import PasswordInput from '../../features/auth/PasswordInput'
import { safeNext } from '../../features/auth/session-helpers'
import { isTransient } from '../../features/auth/link-errors'
import { ACCOUNT_ROLE_LABEL, COMPANY_ROLE_HELP, COMPANY_ROLE_LABEL, ACCOUNT_ROLE_HELP } from '../../features/account/labels'
import { Loading } from '../../ui'

export default function AcceptInvite() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { me, loading, refresh } = useSession()
  const q = useQuery({ queryKey: ['invitation', token], queryFn: () => api.get<InvitationPreviewDto>(`/invitations/${token}`), retry: false })
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wrongUser, setWrongUser] = useState(false)

  if (q.isLoading || loading) return <AuthShell title="Convite"><Loading lines={3} /></AuthShell>
  if (q.error || !q.data) {
    const transient = isTransient(q.error)
    return (
      <AuthShell title={transient ? 'Não foi possível abrir o convite' : 'Este convite não vale mais'} sub={transient ? errorMessage(q.error) : 'O link pode estar incompleto, ter expirado ou sido cancelado. Peça um novo convite a quem convidou você.'}>
        <div className="stack" style={{ gap: 10 }}>
          {transient && <button type="button" className="btn primary block" onClick={() => void q.refetch()}>Tentar de novo</button>}
          {!me && !transient && <Link className="btn primary block" to="/login">Entrar</Link>}
          <Link className={`btn block ${me ? 'primary' : ''}`} to={me ? '/app' : '/'}>{me ? 'Ir para o painel' : 'Página inicial'}</Link>
        </div>
      </AuthShell>
    )
  }
  const inv = q.data
  const here = `/convite/${token}`
  const where = inv.companyName ? <>a empresa <b>{inv.companyName}</b> ({inv.accountName})</> : <>a conta <b>{inv.accountName}</b></>
  const roleLabel = inv.companyRole ? COMPANY_ROLE_LABEL[inv.companyRole] : inv.accountRole ? ACCOUNT_ROLE_LABEL[inv.accountRole] : ''
  const roleHelp = inv.companyRole ? COMPANY_ROLE_HELP[inv.companyRole] : inv.accountRole ? ACCOUNT_ROLE_HELP[inv.accountRole] : ''
  const loggedAsOther = wrongUser || (!!me && me.user.email.toLowerCase() !== inv.email.toLowerCase())

  async function accept(e?: FormEvent) {
    e?.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<AcceptInvitationResponse>(`/invitations/${token}/accept`, me ? {} : { name, password })
      await refresh()
      navigate(safeNext(r.next), { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'login_required') navigate(`/login?next=${encodeURIComponent(here)}`)
      else if (err instanceof ApiError && err.code === 'invite_wrong_user') setWrongUser(true)
      else if (err instanceof ApiError && err.code === 'invite_wrong_user') setWrongUser(true)
      else setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (inv.status !== 'pending') {
    const msg = inv.status === 'expired' ? 'Este convite expirou.' : inv.status === 'accepted' ? 'Este convite já foi aceito.' : 'Este convite foi cancelado.'
    return (
      <AuthShell title={msg} sub={inv.status === 'accepted' ? 'Entre com seu e-mail e senha para acessar.' : `Peça um novo convite a ${inv.invitedByName ?? 'quem convidou você'}.`}>
        <Link className="btn primary block" to={me ? '/app' : '/login'}>{me ? 'Ir para o painel' : 'Entrar'}</Link>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Você recebeu um convite" sub={<>{inv.invitedByName ?? 'Alguém'} convidou <b>{inv.email}</b> para {where} no Seu Impulso.</>}>
      <div className="callout" style={{ marginBottom: 16 }}>
        <div><div className="strong">Papel: {roleLabel}</div><div className="small muted">{roleHelp}</div></div>
      </div>
      <FormError error={error} />
      {loggedAsOther ? (
        <div className="stack mt-12" style={{ gap: 12 }}>
          <p className="small">Você entrou como <b>{me?.user.email}</b>, mas o convite é para <b>{inv.email}</b>.</p>
          <Link className="btn primary block" to="/sair">Sair e entrar com {inv.email}</Link>
        </div>
      ) : me ? (
        <button className="btn primary lg block mt-12" disabled={busy} onClick={() => accept()}>{busy ? 'Entrando…' : 'Aceitar convite'}</button>
      ) : inv.userExists ? (
        <div className="stack mt-12" style={{ gap: 12 }}>
          <p className="small muted">Você já tem login no Seu Impulso. Entre para aceitar.</p>
          <Link className="btn primary lg block" to={`/login?next=${encodeURIComponent(here)}`}>Entrar e aceitar</Link>
        </div>
      ) : (
        <form className="stack mt-12" style={{ gap: 14 }} onSubmit={accept}>
          <div className="field">
            <label htmlFor="name">Seu nome</label>
            <input id="name" className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
          </div>
          <div className="field">
            <label htmlFor="password">Crie uma senha</label>
            <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" describedBy="password-hint" minLength={8} required />
            <p id="password-hint" className="small muted auth-hint">Pelo menos 8 caracteres.</p>
          </div>
          <p className="small muted">Ao continuar, você aceita os <Link to="/termos" target="_blank" className="strong">termos de uso</Link> e a <Link to="/privacidade" target="_blank" className="strong">política de privacidade</Link>.</p>
          <button className="btn primary lg block" disabled={busy}>{busy ? 'Criando acesso…' : 'Aceitar e entrar'}</button>
        </form>
      )}
    </AuthShell>
  )
}
